import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useEntities } from "@/lib/queries";
import { IngredientRows, StepRows, blankIng } from "@/components/recipes/IngredientRows";

const Field = ({ label, children, className = "" }) => <label className={`block space-y-1.5 ${className}`}><span className="lf-label">{label}</span>{children}</label>;
const toInt = (v) => (v === "" || v == null ? null : Math.max(0, parseInt(v, 10) || 0));

const FoodLink = ({ uid, value, onChange }) => {
  const { data: ents = [] } = useEntities(uid);
  const foods = ents.filter((e) => e.category === "Food");
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger className="h-9" data-testid="recipe-food-link"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Not linked</SelectItem>
        {foods.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`recipe-food-option-${e.id}`}>{e.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const RecipeEditor = ({ recipe, universeId, entityId, onSaved, onCancel }) => {
  const [r, setR] = useState(() => ({ title: "", description: "", servings: 4, prep_minutes: "", cook_minutes: "", ingredients: [blankIng()], steps: [""], notes: "", tags: [], entity_id: entityId || null, ...recipe }));
  const [tags, setTags] = useState((recipe?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const set = (patch) => setR((x) => ({ ...x, ...patch }));
  const uid = recipe?.universe_id ?? universeId;
  const save = async () => {
    const body = { title: r.title.trim(), description: r.description, servings: Math.max(1, parseInt(r.servings, 10) || 1), prep_minutes: toInt(r.prep_minutes), cook_minutes: toInt(r.cook_minutes),
      ingredients: r.ingredients.filter((i) => i.name.trim()).map((i) => ({ ...i, name: i.name.trim() })), steps: r.steps, notes: r.notes, tags: tags.split(",").map((t) => t.trim()).filter(Boolean), entity_id: uid ? r.entity_id : null };
    if (!body.title) return toast.error("Give your recipe a name");
    setBusy(true);
    try {
      const { data } = recipe?.id ? await api.patch(`/recipes/${recipe.id}`, body) : await api.post("/recipes", { ...body, universe_id: uid || null });
      toast.success(recipe?.id ? "Recipe saved" : "Recipe created");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-4xl space-y-8 animate-in fade-in duration-300" data-testid="recipe-editor">
      <Field label="Recipe name"><Input value={r.title} onChange={(e) => set({ title: e.target.value })} maxLength={120} placeholder="e.g. Grandmother Vel's honey bread" className="h-11 font-display text-xl" data-testid="recipe-title-input" /></Field>
      <Field label="About this dish"><Textarea value={r.description} onChange={(e) => set({ description: e.target.value })} maxLength={4000} rows={3} placeholder="Where it comes from, who makes it, when it's eaten…" data-testid="recipe-description-input" /></Field>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Serves"><Input type="number" min={1} max={1000} value={r.servings} onChange={(e) => set({ servings: e.target.value })} className="h-9" data-testid="recipe-servings-input" /></Field>
        <Field label="Prep (min)"><Input type="number" min={0} value={r.prep_minutes ?? ""} onChange={(e) => set({ prep_minutes: e.target.value })} className="h-9" data-testid="recipe-prep-input" /></Field>
        <Field label="Cook (min)"><Input type="number" min={0} value={r.cook_minutes ?? ""} onChange={(e) => set({ cook_minutes: e.target.value })} className="h-9" data-testid="recipe-cook-input" /></Field>
        {uid && <Field label="Food note"><FoodLink uid={uid} value={r.entity_id} onChange={(v) => set({ entity_id: v })} /></Field>}
      </div>
      <Field label="Ingredients"><IngredientRows list={r.ingredients} onChange={(ingredients) => set({ ingredients })} /></Field>
      <Field label="Steps"><StepRows list={r.steps} onChange={(steps) => set({ steps })} /></Field>
      <Field label="Notes"><Textarea value={r.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={6000} rows={3} placeholder="Serving ideas, story notes, variations…" data-testid="recipe-notes-input" /></Field>
      <Field label="Tags (comma separated)"><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="feast, dessert, northern" className="h-9" data-testid="recipe-tags-input" /></Field>
      <div className="flex gap-2 border-t border-[#2D3442] pt-6">
        <Button onClick={save} disabled={busy} data-testid="recipe-save-button">{busy ? "Saving…" : "Save recipe"}</Button>
        <Button variant="ghost" onClick={onCancel} data-testid="recipe-cancel-button">Cancel</Button>
      </div>
    </div>
  );
};
